import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { createClient } from '@supabase/supabase-js';
import { useAuth } from '../contexts/AuthContext';
import { useAuditLog } from './useAuditLog';
import { useDataSync } from './useDataSync';
import { dataCache, CacheKeys, CacheConfigs } from '../utils/dataCache';

// Cliente service role para sessões de passkey
const supabaseServiceRole = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SERVICE_ROLE_KEY || ''
);

export interface AccessPermission {
  id: string;
  patient_id: string;
  requester_id: string; // Corrigido: era doctor_id
  record_id?: string;
  permission_type: 'read' | 'write' | 'download' | 'share' | 'delete';
  status: 'pending' | 'approved' | 'denied' | 'revoked';
  granted_at?: string;
  expires_at?: string;
  revoked_at?: string;
  blockchain_tx_id?: string;
  smart_contract_address?: string;
  created_at: string;
  updated_at: string;
}

export interface AccessRequest {
  id: string;
  patient_id: string;
  requester_id: string; // Corrigido: era doctor_id
  record_id?: string;
  permission_type: AccessPermission['permission_type'];
  status: AccessPermission['status'];
  justification: string;
  requested_duration?: string;
  created_at: string;
  expires_at?: string;
  granted_at?: string;
}

export interface PatientAccessSettings {
  id: string;
  patient_id: string;
  auto_approve_doctors: boolean;
  auto_approve_duration: string;
  require_justification: boolean;
  allow_emergency_access: boolean;
  notification_preferences: Record<string, any>;
}

export const useAccessControl = () => {
  const { user, userProfile } = useAuth();
  const { logAudit } = useAuditLog();
  const { registerRevalidation } = useDataSync();
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState<AccessPermission[]>([]);
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [settings, setSettings] = useState<PatientAccessSettings | null>(null);

  // Debug: Log user info when it changes
  useEffect(() => {
    if (user) {
      console.log('🔍 [DEBUG] useAccessControl - User info:', {
        id: user.id,
        email: user.email,
        role: userProfile?.role
      });
    } else {
      console.log('🔍 [DEBUG] useAccessControl - No user found');
    }
  }, [user, userProfile]);

  // Verificar se usuário tem permissão para acessar dados específicos
  const hasPermission = async (
    patientId: string,
    permissionType: AccessPermission['permission_type'],
    recordId?: string
  ): Promise<boolean> => {
    if (!user) return false;
    
    // Paciente sempre tem acesso aos próprios dados
    if (user.id === patientId) return true;

    try {
      const query = supabase
        .from('access_permissions')
        .select('*')
        .eq('patient_id', patientId)
        .eq('requester_id', user.id)
        .eq('permission_type', permissionType)
        .eq('status', 'approved');

      if (recordId) {
        query.eq('record_id', recordId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao verificar permissão:', error);
        return false;
      }

      // Verificar se a permissão não expirou
      const validPermissions = data?.filter(permission => {
        if (!permission.expires_at) return true;
        return new Date(permission.expires_at) > new Date();
      });

      return validPermissions && validPermissions.length > 0;
    } catch (error) {
      console.error('Erro ao verificar permissão:', error);
      return false;
    }
  };

  // Solicitar acesso a dados de paciente
  const requestAccess = async (
    patientId: string,
    permissionTypes: AccessPermission['permission_type'][] | AccessPermission['permission_type'],
    dataTypes: string[] | string,
    justification: string,
    requestedDuration?: string,
    isEmergency?: boolean,
    recordId?: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: 'Usuário não autenticado' };

    setLoading(true);
    try {
      // Normalizar arrays
      const permissionTypesArray = Array.isArray(permissionTypes) ? permissionTypes : [permissionTypes];
      const dataTypesArray = Array.isArray(dataTypes) ? dataTypes : [dataTypes];

      // Verificar se já existe uma solicitação pendente similar
      const { data: existingRequests } = await supabase
        .from('access_requests')
        .select('*')
        .eq('patient_id', patientId)
        .eq('requester_id', user.id)
        .eq('status', 'pending');

      // Verificar se há conflito com solicitações existentes
      const hasConflict = existingRequests?.some(req => 
        permissionTypesArray.includes(req.permission_type) &&
        dataTypesArray.includes(req.data_type)
      );

      if (hasConflict) {
        return { success: false, error: 'Já existe uma solicitação pendente para este tipo de acesso' };
      }

      // Criar solicitações apenas para as combinações específicas
      const requests = [];
      
      // Se os arrays têm o mesmo tamanho, são combinações específicas
      if (permissionTypesArray.length === dataTypesArray.length) {
        for (let i = 0; i < permissionTypesArray.length; i++) {
          const requestData = {
            patient_id: patientId,
            requester_id: user.id,
            permission_type: permissionTypesArray[i],
            data_type: dataTypesArray[i],
            justification: justification,
            requested_duration: requestedDuration === 'never' ? null : requestedDuration,
            status: 'pending' as const,
            ...(isEmergency && { priority: 'emergency' })
          };
          requests.push(requestData);
        }
      } else {
        // Fallback: produto cartesiano para compatibilidade com chamadas antigas
        for (const permissionType of permissionTypesArray) {
          for (const dataType of dataTypesArray) {
            const requestData = {
              patient_id: patientId,
              requester_id: user.id,
              permission_type: permissionType,
              data_type: dataType,
              justification: justification,
              requested_duration: requestedDuration === 'never' ? null : requestedDuration,
              status: 'pending' as const,
              ...(isEmergency && { priority: 'emergency' })
            };
            requests.push(requestData);
          }
        }
      }

      // Inserir todas as solicitações
      const { data, error } = await supabase
        .from('access_requests')
        .insert(requests)
        .select();

      if (error) {
        console.error('Erro ao criar solicitações:', error);
        return { success: false, error: 'Erro ao criar solicitações de acesso' };
      }

      // Log da ação removido temporariamente devido a erro de rede
      // TODO: Reativar auditoria após correção das políticas RLS
      console.log(`🔍 [AUDIT] ${data.length} solicitações criadas localmente para paciente ${patientId}`);

      // Invalidar cache de solicitações
      dataCache.delete(CacheKeys.REQUESTS(user.id));
      dataCache.delete(CacheKeys.REQUESTS(patientId));
      console.log('🔄 Cache de solicitações invalidado após criação');

      // Criar notificação consolidada para o paciente
      const permissionLabels = permissionTypesArray.map(p => {
        switch(p) {
          case 'read': return 'visualizar';
          case 'write': return 'editar';
          case 'download': return 'baixar';
          case 'share': return 'compartilhar';
          case 'delete': return 'excluir';
          default: return p;
        }
      }).join(', ');

      const dataLabels = dataTypesArray.map(d => {
        switch(d) {
          case 'all': return 'todos os dados';
          case 'medical_records': return 'registros médicos';
          case 'documents': return 'documentos';
          case 'lab_results': return 'resultados de exames';
          case 'prescriptions': return 'prescrições';
          case 'imaging': return 'exames de imagem';
          case 'personal_info': return 'informações pessoais';
          default: return d;
        }
      }).join(', ');

      await supabase
        .from('access_notifications')
        .insert({
          user_id: patientId,
          type: 'access_request',
          title: isEmergency ? 'Solicitação de acesso de emergência' : 'Nova solicitação de acesso',
          message: `${userProfile?.full_name || 'Um médico'} solicitou permissão para ${permissionLabels} ${dataLabels}.${isEmergency ? ' Esta é uma solicitação de emergência.' : ''}`,
          related_request_id: data[0].id
        });

      await loadRequests();
      return { success: true };
    } catch (error) {
      console.error('Erro ao solicitar acesso:', error);
      return { success: false, error: 'Erro interno do servidor' };
    } finally {
      setLoading(false);
    }
  };

  // Aprovar ou negar solicitação de acesso
  const reviewAccessRequest = async (
    requestId: string,
    action: 'approved' | 'denied',
    reviewNotes?: string,
    customDuration?: string,
    expiresAt?: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: 'Usuário não autenticado' };

    setLoading(true);
    try {
      // Calcular data de expiração baseada na duração customizada ou padrão
      let calculatedExpiresAt = expiresAt;
      if (action === 'approved' && customDuration && !expiresAt) {
        // Verificar se é duração 'never' (permanente)
        if (customDuration === 'never') {
          calculatedExpiresAt = null; // Acesso permanente
        } else {
          const now = new Date();
          const match = customDuration.match(/(\d+)\s+(hours?|days?|weeks?|months?)/);
          if (match) {
            const value = parseInt(match[1]);
            const unit = match[2].replace(/s$/, '');
            
            switch (unit) {
              case 'hour':
                now.setHours(now.getHours() + value);
                break;
              case 'day':
                now.setDate(now.getDate() + value);
                break;
              case 'week':
                now.setDate(now.getDate() + (value * 7));
                break;
              case 'month':
                now.setMonth(now.getMonth() + value);
                break;
            }
            calculatedExpiresAt = now.toISOString();
          }
        }
      }

      // Primeiro, atualizar a solicitação na tabela access_requests
      const { data: requestData, error: requestError } = await supabase
        .from('access_requests')
        .update({
          status: action,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
          review_notes: reviewNotes,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .eq('patient_id', user.id) // Garantir que apenas o paciente pode aprovar
        .select()
        .single();

      if (requestError) {
        console.error('Erro ao atualizar solicitação:', requestError);
        return { success: false, error: 'Erro ao atualizar solicitação' };
      }

      // Se aprovado, criar uma permissão na tabela access_permissions
      let data = requestData;
      if (action === 'approved') {
        const permissionData = {
          patient_id: requestData.patient_id,
          requester_id: requestData.requester_id,
          permission_type: requestData.permission_type,
          data_type: requestData.data_type,
          status: 'approved',
          granted_at: new Date().toISOString(),
          expires_at: calculatedExpiresAt
        };

        const { data: permissionResult, error: permissionError } = await supabase
          .from('access_permissions')
          .insert([permissionData])
          .select()
          .single();

        if (permissionError) {
          console.error('Erro ao criar permissão:', permissionError);
          return { success: false, error: 'Erro ao criar permissão' };
        }

        data = permissionResult;
      }



      // Log da ação removido temporariamente devido a erro de rede
      // TODO: Reativar auditoria após correção das políticas RLS
      console.log(`🔍 [AUDIT] Ação registrada localmente: ${action} para solicitação ${requestId}`);

      // Criar notificação para o médico
      await supabase
        .from('access_notifications')
        .insert({
          user_id: data.requester_id,
          type: 'access_response',
          title: `Solicitação ${action === 'approved' ? 'aprovada' : 'negada'}`,
          message: `Sua solicitação de acesso foi ${action === 'approved' ? 'aprovada' : 'negada'} pelo paciente.`,
          related_request_id: requestId
        });

      // Invalidar cache relevante de forma mais robusta
      try {
        // Cache do paciente (quem aprovou/negou)
        dataCache.delete(CacheKeys.REQUESTS(user.id));
        dataCache.delete(CacheKeys.PERMISSIONS(user.id));
        
        // Cache do médico (quem solicitou)
        if (requestData.requester_id) {
          dataCache.delete(CacheKeys.REQUESTS(requestData.requester_id));
          dataCache.delete(CacheKeys.PERMISSIONS(requestData.requester_id));
        }
        
        // Invalidar padrões relacionados para garantir limpeza completa
        dataCache.invalidatePattern(`(permissions|requests)_.*`);
        
        console.log('🔄 Cache invalidado após revisão de solicitação');
      } catch (cacheError) {
        console.warn('⚠️ Erro ao invalidar cache (não crítico):', cacheError);
      }

      await loadPermissions();
      await loadRequests();
      return { success: true };
    } catch (error) {
      console.error('Erro ao revisar solicitação:', error);
      return { success: false, error: 'Erro interno do servidor' };
    } finally {
      setLoading(false);
    }
  };

  // Atualizar detalhes completos de permissão existente (apenas paciente)
  const updatePermissionDetails = async (
    permissionId: string,
    updates: {
      duration?: string;
      permissionTypes?: string[];
      dataTypes?: string[];
    }
  ): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: 'Usuário não autenticado' };

    setLoading(true);
    try {
      // Preparar dados para atualização
      const updateData: any = {
        updated_at: new Date().toISOString()
      };

      // Atualizar duração se fornecida
      if (updates.duration) {
        if (updates.duration === 'never') {
          updateData.expires_at = null; // Acesso permanente
        } else {
          // Calcular nova data de expiração
          const match = updates.duration.match(/(\d+)\s*(day|week|month)s?/);
          if (!match) {
            return { success: false, error: 'Formato de duração inválido' };
          }

          const [, valueStr, unit] = match;
          const value = parseInt(valueStr, 10);
          const now = new Date();

          switch (unit) {
            case 'day':
              now.setDate(now.getDate() + value);
              break;
            case 'week':
              now.setDate(now.getDate() + (value * 7));
              break;
            case 'month':
              now.setMonth(now.getMonth() + value);
              break;
            default:
              return { success: false, error: 'Unidade de tempo inválida' };
          }

          updateData.expires_at = now.toISOString();
        }
      }

      // Atualizar tipo de permissão se fornecido
      if (updates.permissionTypes && updates.permissionTypes.length > 0) {
        updateData.permission_type = updates.permissionTypes[0]; // Assumindo um tipo por vez
      }

      // Atualizar tipo de dados se fornecido
      if (updates.dataTypes && updates.dataTypes.length > 0) {
        updateData.data_type = updates.dataTypes[0]; // Assumindo um tipo por vez
      }

      const { data, error } = await supabase
        .from('access_permissions')
        .update(updateData)
        .eq('id', permissionId)
        .eq('patient_id', user.id) // Garantir que apenas o paciente pode alterar
        .eq('status', 'approved') // Apenas permissões ativas
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar detalhes da permissão:', error);
        return { success: false, error: 'Erro ao atualizar detalhes da permissão' };
      }

      // Log da ação removido temporariamente devido a erro de rede
      // TODO: Reativar auditoria após correção das políticas RLS
      console.log(`🔍 [AUDIT] Atualização registrada localmente: permissão ${permissionId} atualizada pelo paciente`);

      // Criar notificação para o médico
      const updateDescription = [];
      if (updates.duration) updateDescription.push(`duração: ${updates.duration}`);
      if (updates.permissionTypes) updateDescription.push(`tipo: ${updates.permissionTypes.join(', ')}`);
      if (updates.dataTypes) updateDescription.push(`dados: ${updates.dataTypes.join(', ')}`);

      await supabase
        .from('access_notifications')
        .insert({
          user_id: data.requester_id,
          type: 'permission_updated',
          title: 'Permissão de acesso alterada',
          message: `${userProfile?.full_name || 'O paciente'} alterou sua permissão de acesso (${updateDescription.join(', ')}).`,
          related_permission_id: permissionId
        });

      // Invalidar cache de permissões
      dataCache.delete(CacheKeys.PERMISSIONS(user.id));
      console.log('🔄 Cache de permissões invalidado após atualização completa');

      await loadPermissions();
      return { success: true };
    } catch (error) {
      console.error('Erro ao atualizar detalhes da permissão:', error);
      return { success: false, error: 'Erro interno do servidor' };
    } finally {
      setLoading(false);
    }
  };

  // Atualizar duração de permissão existente (apenas paciente)
  const updatePermissionDuration = async (
    permissionId: string,
    newDuration: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: 'Usuário não autenticado' };

    setLoading(true);
    try {
      // Calcular nova data de expiração
      let newExpiresAt: string | null = null;
      
      // Verificar se é duração 'never' (permanente)
      if (newDuration === 'never') {
        newExpiresAt = null; // Acesso permanente
      } else {
        const now = new Date();
        const match = newDuration.match(/(\d+)\s+(hours?|days?|weeks?|months?)/);
        if (!match) {
          return { success: false, error: 'Formato de duração inválido' };
        }

        const value = parseInt(match[1]);
        const unit = match[2].replace(/s$/, '');
        
        switch (unit) {
          case 'hour':
            now.setHours(now.getHours() + value);
            break;
          case 'day':
            now.setDate(now.getDate() + value);
            break;
          case 'week':
            now.setDate(now.getDate() + (value * 7));
            break;
          case 'month':
            now.setMonth(now.getMonth() + value);
            break;
          default:
            return { success: false, error: 'Unidade de tempo inválida' };
        }

        newExpiresAt = now.toISOString();
      }

      const { data, error } = await supabase
        .from('access_permissions')
        .update({
          expires_at: newExpiresAt,
          updated_at: new Date().toISOString()
        })
        .eq('id', permissionId)
        .eq('patient_id', user.id) // Garantir que apenas o paciente pode alterar
        .eq('status', 'approved') // Apenas permissões ativas
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar duração da permissão:', error);
        return { success: false, error: 'Erro ao atualizar duração da permissão' };
      }

      // Log da ação removido temporariamente devido a erro de rede
      // TODO: Reativar auditoria após correção das políticas RLS
      console.log(`🔍 [AUDIT] Duração da permissão ${permissionId} atualizada localmente para ${newDuration}`);

      // Criar notificação para o médico
      await supabase
        .from('access_notifications')
        .insert({
          user_id: data.requester_id,
          type: 'permission_updated',
          title: 'Duração de permissão alterada',
          message: `${userProfile?.full_name || 'O paciente'} alterou a duração da sua permissão de acesso para ${newDuration}.`,
          related_permission_id: permissionId
        });

      // Invalidar cache de permissões
      dataCache.delete(CacheKeys.PERMISSIONS(user.id));
      console.log('🔄 Cache de permissões invalidado após atualização de duração');

      await loadPermissions();
      return { success: true };
    } catch (error) {
      console.error('Erro ao atualizar duração da permissão:', error);
      return { success: false, error: 'Erro interno do servidor' };
    } finally {
      setLoading(false);
    }
  };

  // Revogar permissão existente
  const revokePermission = async (
    permissionId: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!user) return { success: false, error: 'Usuário não autenticado' };

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('access_permissions')
        .update({
          status: 'revoked',
          revoked_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', permissionId)
        .eq('patient_id', user.id) // Garantir que apenas o paciente pode revogar
        .select()
        .single();

      if (error) {
        console.error('Erro ao revogar permissão:', error);
        return { success: false, error: 'Erro ao revogar permissão' };
      }

      // Log da ação removido temporariamente devido a erro de rede
      // TODO: Reativar auditoria após correção das políticas RLS
      console.log(`🔍 [AUDIT] Permissão ${permissionId} revogada localmente pelo usuário ${user.id}`);

      // Criar notificação para o médico
      await supabase
        .from('access_notifications')
        .insert({
          user_id: data.requester_id,
          type: 'access_revoked',
          title: 'Acesso revogado',
          message: 'O paciente revogou sua permissão de acesso aos dados médicos.',
          related_permission_id: permissionId
        });

      // Invalidar cache de permissões
      dataCache.delete(CacheKeys.PERMISSIONS(user.id));
      console.log('🔄 Cache de permissões invalidado após revogação');

      await loadPermissions();
      return { success: true };
    } catch (error) {
      console.error('Erro ao revogar permissão:', error);
      return { success: false, error: 'Erro interno do servidor' };
    } finally {
      setLoading(false);
    }
  };

  // Criar notificação
  const createNotification = async (
    userId: string,
    type: string,
    title: string,
    message: string,
    relatedId?: string
  ) => {
    try {
      await supabase
        .from('access_notifications')
        .insert([{
          user_id: userId,
          type,
          title,
          message,
          related_permission_id: relatedId,
          created_at: new Date().toISOString()
        }]);
    } catch (error) {
      console.error('Erro ao criar notificação:', error);
    }
  };

  // Função para detectar se é sessão de passkey
  const isPasskeySession = () => {
    try {
      // Verificar se há dados de passkey no localStorage (chave correta)
      const passkeyData = localStorage.getItem('saudevirtual_passkey_session');
      if (passkeyData) {
        const sessionData = JSON.parse(passkeyData);
        // Verificar se a sessão ainda é válida
        if (sessionData.expiresAt && Date.now() < sessionData.expiresAt) {
          console.log('✅ [useAccessControl] Sessão de passkey válida detectada');
          return true;
        } else {
          console.log('⚠️ [useAccessControl] Sessão de passkey expirada');
          return false;
        }
      }
      return false;
    } catch (error) {
      console.warn('⚠️ [useAccessControl] Erro ao verificar sessão de passkey:', error);
      return false;
    }
  };

  // Função para obter o cliente Supabase apropriado
  const getSupabaseClient = () => {
    const isPasskey = isPasskeySession();
    console.log('🔍 [DEBUG] getSupabaseClient - isPasskey:', isPasskey);
    
    if (isPasskey) {
      console.log('🔑 [useAccessControl] Usando service role para sessão de passkey');
      return supabaseServiceRole;
    } else {
      console.log('🔐 [useAccessControl] Usando anon key para sessão tradicional');
      return supabase;
    }
  };

  // Carregar permissões do usuário
  const loadPermissions = async () => {
    console.log('🚀 [useAccessControl] loadPermissions called');
    if (!user || !userProfile) {
      console.log('⚠️ [useAccessControl] loadPermissions: user ou userProfile não disponível');
      console.log('🔍 [DEBUG] loadPermissions - user:', user ? {
        id: user.id,
        email: user.email,
        type: typeof user.id,
        idLength: user.id?.length
      } : 'null');
      console.log('🔍 [DEBUG] loadPermissions - userProfile:', userProfile ? {
        role: userProfile.role,
        id: userProfile.id,
        type: typeof userProfile.id,
        idLength: userProfile.id?.length
      } : 'null');
      return;
    }

    console.log(`🔄 [useAccessControl] Carregando permissões para user: ${user.id}, role: ${userProfile.role}`);
    console.log('🔍 [DEBUG] loadPermissions - Iniciando carregamento de permissões...');
    const cacheKey = CacheKeys.PERMISSIONS(user.id);
    
    // Tentar carregar do cache primeiro (mas não confiar totalmente após reversões)
    try {
      const cachedData = dataCache.get<AccessPermission[]>(cacheKey);
      if (cachedData && cachedData.length > 0) {
        console.log('📦 [useAccessControl] Permissões carregadas do cache:', cachedData.length);
        setPermissions(cachedData);
        // Ainda assim, verificar se há dados mais recentes no banco
      } else {
        console.log('📦 [useAccessControl] Cache vazio ou inexistente, buscando no banco');
      }
    } catch (cacheError) {
      console.warn('⚠️ [useAccessControl] Erro ao acessar cache de permissões:', cacheError);
    }

    try {
      console.log('🌐 [useAccessControl] Carregando permissões do banco de dados...');
      
      // Obter cliente apropriado baseado no tipo de sessão
      const clientToUse = getSupabaseClient();
      const isPasskey = isPasskeySession();
      
      // Verificar se service role está disponível
      if (isPasskey && !import.meta.env.VITE_SERVICE_ROLE_KEY) {
        console.error('❌ [useAccessControl] VITE_SERVICE_ROLE_KEY não está definida para sessão de passkey');
        setPermissions([]);
        return;
      }
      
      // Executar query com o cliente apropriado
      let data, error;
      try {
        console.log('🔍 [DEBUG] loadPermissions - Executando query com user.id:', user.id);
        console.log('🔍 [DEBUG] loadPermissions - Cliente:', isPasskey ? 'service role' : 'anon key');
        console.log('🔍 [DEBUG] loadPermissions - Query filter:', `patient_id.eq.${user.id},requester_id.eq.${user.id}`);
        
        const result = await clientToUse
          .from('access_permissions')
          .select(`
            *,
            patient:patient_id(id, full_name, email, role),
            requester:requester_id(id, full_name, email, role)
          `)
          .or(`patient_id.eq.${user.id},requester_id.eq.${user.id}`)
          .order('created_at', { ascending: false });
          
        console.log('🔍 [DEBUG] loadPermissions - Query result:', {
          data: result.data?.length || 0,
          error: result.error,
          status: result.status,
          statusText: result.statusText
        });
        
        data = result.data;
        error = result.error;
        
        if (!error) {
          console.log('✅ [useAccessControl] Query executada com sucesso!');
        }
      } catch (joinError) {
        console.warn('⚠️ [useAccessControl] Query com joins falhou, usando fallback:', joinError);
        
        // Fallback: consultas separadas (mantém robustez)
        const result = await clientToUse
          .from('access_permissions')
          .select('*')
          .or(`patient_id.eq.${user.id},requester_id.eq.${user.id}`)
          .order('created_at', { ascending: false });
        
        data = result.data;
        error = result.error;
        
        if (!error && data) {
          // Buscar perfis dos usuários relacionados
          const userIds = [...new Set([
            ...data.map(p => p.patient_id),
            ...data.map(p => p.requester_id)
          ])].filter(Boolean);

          let profilesData: any[] = [];
          if (userIds.length > 0) {
            const { data: profiles, error: profilesError } = await clientToUse
              .from('user_profiles')
              .select('id, full_name, email, role')
              .in('id', userIds);

            if (profilesError) {
              console.error('❌ [useAccessControl] Erro ao carregar perfis:', profilesError);
            } else {
              profilesData = profiles || [];
            }
          }

          // Mapear permissões com perfis manualmente
          data = data.map(permission => {
            const patientProfile = profilesData.find(p => p.id === permission.patient_id);
            const requesterProfile = profilesData.find(p => p.id === permission.requester_id);
            
            return {
              ...permission,
              patient: patientProfile || { 
                id: permission.patient_id, 
                full_name: 'Paciente não encontrado', 
                email: '', 
                role: 'patient' 
              },
              requester: requesterProfile || { 
                id: permission.requester_id, 
                full_name: 'Médico não encontrado', 
                email: '', 
                role: 'doctor' 
              }
            };
          });
        }
      }
      
      console.log('🔍 [useAccessControl] Resultado da busca de permissões:', { data: data?.length, error });
      if (data) {
        console.log('🔍 [useAccessControl] Primeiras 3 permissões:', data.slice(0, 3));
        // Log específico para médicos
        if (userProfile.role === 'doctor') {
          const doctorPermissions = data.filter(p => p.requester_id === user.id && p.status === 'approved');
          console.log('🔍 [useAccessControl] Permissões aprovadas para médico:', doctorPermissions.length);
          console.log('🔍 [useAccessControl] Detalhes permissões médico:', doctorPermissions);
        }
      }

      if (error) {
        console.error('❌ [useAccessControl] Erro ao carregar permissões:', error);
        return;
      }

      if (!data || data.length === 0) {
        console.log('ℹ️ [useAccessControl] Nenhuma permissão encontrada');
        setPermissions([]);
        dataCache.set(cacheKey, [], CacheConfigs.NORMAL);
        return;
      }

      const permissionsWithProfiles = data;

      setPermissions(permissionsWithProfiles);
       
       // Armazenar no cache
       dataCache.set(cacheKey, permissionsWithProfiles, CacheConfigs.NORMAL);
       console.log('✅ [useAccessControl] Permissões carregadas e armazenadas no cache:', permissionsWithProfiles.length);
       console.log('📊 [useAccessControl] Dados das permissões:', permissionsWithProfiles.map(p => ({
         id: p.id,
         status: p.status,
         patient: p.patient?.full_name || 'N/A',
         requester: p.requester?.full_name || 'N/A'
       })));
    } catch (error) {
      console.error('❌ [useAccessControl] Erro crítico ao carregar permissões:', error);
      // Fallback: definir array vazio para evitar undefined
      setPermissions([]);
    }
  };

  // Carregar solicitações com filtro opcional
  const loadRequests = async (statusFilter?: string | 'all') => {
    console.log('🚀 [useAccessControl] loadRequests called with filter:', statusFilter);
    if (!user || !userProfile) {
      console.log('⚠️ [useAccessControl] loadRequests: user ou userProfile não disponível');
      console.log('🔍 [DEBUG] loadRequests - user:', user ? {
        id: user.id,
        email: user.email,
        type: typeof user.id,
        idLength: user.id?.length
      } : 'null');
      console.log('🔍 [DEBUG] loadRequests - userProfile:', userProfile ? {
        role: userProfile.role,
        id: userProfile.id,
        type: typeof userProfile.id,
        idLength: userProfile.id?.length
      } : 'null');
      return;
    }

    console.log(`🔄 [useAccessControl] Carregando solicitações para user: ${user.id}, role: ${userProfile.role}`);
    console.log('🔍 [DEBUG] loadRequests - Iniciando carregamento de solicitações...');

    const cacheKey = statusFilter && statusFilter !== 'all' 
      ? `${CacheKeys.REQUESTS(user.id)}_${statusFilter}`
      : CacheKeys.REQUESTS(user.id);
    
    // Tentar carregar do cache primeiro
    try {
      const cachedData = dataCache.get<AccessRequest[]>(cacheKey);
      if (cachedData) {
        console.log('📦 Solicitações carregadas do cache');
        setRequests(cachedData);
        return;
      }
    } catch (cacheError) {
      console.warn('⚠️ Erro ao acessar cache de solicitações:', cacheError);
    }

    try {
      console.log('🌐 Carregando solicitações do banco de dados...');
      
      // Obter cliente apropriado baseado no tipo de sessão
      const clientToUse = getSupabaseClient();
      const isPasskey = isPasskeySession();
      
      // Verificar se service role está disponível
      if (isPasskey && !import.meta.env.VITE_SERVICE_ROLE_KEY) {
        console.error('❌ [useAccessControl] VITE_SERVICE_ROLE_KEY não está definida para sessão de passkey');
        setRequests([]);
        return;
      }
      
      console.log('🔍 [DEBUG] loadRequests - Executando query com user.id:', user.id);
      console.log('🔍 [DEBUG] loadRequests - Cliente:', isPasskey ? 'service role' : 'anon key');
      console.log('🔍 [DEBUG] loadRequests - Query filter:', `patient_id.eq.${user.id},requester_id.eq.${user.id}`);
      
      // Construir query base
      let query = clientToUse
        .from('access_requests')
        .select('*')
        .or(`patient_id.eq.${user.id},requester_id.eq.${user.id}`);
      
      // Aplicar filtro de status se especificado
      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
        console.log('🔍 [DEBUG] loadRequests - Status filter applied:', statusFilter);
      }
      
      const { data: requestsData, error } = await query
        .order('created_at', { ascending: false });
        
      console.log('🔍 [DEBUG] loadRequests - Query result:', {
        data: requestsData?.length || 0,
        error: error,
        hasData: !!requestsData,
        firstItem: requestsData?.[0]
      });

      if (error) {
        console.error('Erro ao carregar solicitações:', error);
        return;
      }

      if (!requestsData || requestsData.length === 0) {
        const emptyRequests: AccessRequest[] = [];
        setRequests(emptyRequests);
        dataCache.set(cacheKey, emptyRequests, CacheConfigs.CRITICAL);
        return;
      }

      // Buscar os perfis dos usuários relacionados
      const userIds = [...new Set([
        ...requestsData.map(r => r.patient_id),
        ...requestsData.map(r => r.requester_id)
      ])].filter(Boolean); // Filtrar IDs nulos/undefined

      let profilesData: any[] = [];
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await clientToUse
          .from('user_profiles')
          .select('id, full_name, email, role')
          .in('id', userIds);

        if (profilesError) {
          console.error('Erro ao carregar perfis:', profilesError);
          // Continuar sem perfis em caso de erro
        } else {
          profilesData = profiles || [];
        }
      }

      // Mapear os dados combinando solicitações com perfis
      const mappedData = requestsData.map(request => {
        const patientProfile = profilesData.find(p => p.id === request.patient_id);
        const requesterProfile = profilesData.find(p => p.id === request.requester_id);
        
        return {
          ...request,
          patient: patientProfile || { 
            id: request.patient_id, 
            full_name: 'Paciente não encontrado', 
            email: '', 
            role: 'patient' 
          },
          requester: requesterProfile || { 
            id: request.requester_id, 
            full_name: 'Médico não encontrado', 
            email: '', 
            role: 'doctor' 
          }
        };
      });

      setRequests(mappedData);
      
      // Armazenar no cache
      dataCache.set(cacheKey, mappedData, CacheConfigs.CRITICAL);
      console.log('✅ Solicitações carregadas e armazenadas no cache');
    } catch (error) {
      console.error('Erro ao carregar solicitações:', error);
    }
  };

  // Carregar configurações do paciente
  const loadPatientSettings = async () => {
    if (!user || userProfile?.role !== 'patient') return;

    const cacheKey = CacheKeys.SETTINGS(user.id);
    
    // Tentar carregar do cache primeiro
    const cachedData = dataCache.get<PatientAccessSettings>(cacheKey);
    if (cachedData) {
      console.log('📦 Configurações carregadas do cache');
      setSettings(cachedData);
      return;
    }

    try {
      console.log('🌐 Carregando configurações do banco de dados...');
      const { data, error } = await supabase
        .from('patient_access_settings')
        .select('*')
        .eq('patient_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao carregar configurações:', error);
        return;
      }

      if (!data) {
        // Criar configurações padrão
        const defaultSettings = {
          patient_id: user.id,
          auto_approve_doctors: false,
          auto_approve_duration: '30 days',
          require_justification: true,
          allow_emergency_access: true,
          notification_preferences: {}
        };

        const { data: newSettings, error: createError } = await supabase
          .from('patient_access_settings')
          .insert([defaultSettings])
          .select()
          .single();

        if (!createError) {
          setSettings(newSettings);
          // Armazenar no cache
          dataCache.set(cacheKey, newSettings, CacheConfigs.STATIC);
          console.log('✅ Configurações padrão criadas e armazenadas no cache');
        }
      } else {
        setSettings(data);
        // Armazenar no cache
        dataCache.set(cacheKey, data, CacheConfigs.STATIC);
        console.log('✅ Configurações carregadas e armazenadas no cache');
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  // Atualizar configurações do paciente
  const updatePatientSettings = async (
    newSettings: Partial<PatientAccessSettings>
  ): Promise<{ success: boolean; error?: string }> => {
    if (!user || !settings) return { success: false, error: 'Configurações não carregadas' };

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('patient_access_settings')
        .update({
          ...newSettings,
          updated_at: new Date().toISOString()
        })
        .eq('id', settings.id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar configurações:', error);
        return { success: false, error: 'Erro ao atualizar configurações' };
      }

      setSettings(data);
      
      // Log da ação removido temporariamente devido a erro de rede
      // TODO: Reativar auditoria após correção das políticas RLS
      console.log(`🔍 [AUDIT] Configurações de acesso ${settings.id} atualizadas localmente`);

      // Invalidar cache de configurações
      dataCache.delete(CacheKeys.SETTINGS(user.id));
      console.log('🔄 Cache de configurações invalidado após atualização');

      return { success: true };
    } catch (error) {
      console.error('Erro ao atualizar configurações:', error);
      return { success: false, error: 'Erro interno do servidor' };
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados quando o usuário mudar
  useEffect(() => {
    console.log('🔄 [useAccessControl] useEffect triggered - user changed');
    console.log('🔍 [DEBUG] useAccessControl - user:', user ? {
      id: user.id,
      email: user.email,
      aud: user.aud,
      hasUserMetadata: !!user.user_metadata,
      hasAppMetadata: !!user.app_metadata
    } : 'null');
    console.log('🔍 [DEBUG] useAccessControl - userProfile:', userProfile ? {
      id: userProfile.id,
      role: userProfile.role,
      email: userProfile.email,
      full_name: userProfile.full_name
    } : 'null');
    
    if (user) {
      console.log('✅ [useAccessControl] User exists, calling load functions...');
      loadPermissions();
      loadRequests();
      loadPatientSettings();
    } else {
      console.log('❌ [useAccessControl] No user, skipping load functions');
    }
  }, [user]);

  // Revalidar dados periodicamente para manter sincronização
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      console.log('🔄 Revalidando dados do useAccessControl...');
      loadPermissions();
      loadRequests();
      loadPatientSettings();
    }, 30000); // Revalidar a cada 30 segundos

    return () => clearInterval(interval);
  }, [user]);

  // Função para forçar revalidação manual
  const revalidateData = async (clearCache = false) => {
    if (!user) return;
    
    if (clearCache) {
      console.log('🔄 Revalidação manual com limpeza de cache...');
      // Invalidar cache específico do usuário
      dataCache.delete(CacheKeys.PERMISSIONS(user.id));
      dataCache.delete(CacheKeys.REQUESTS(user.id));
      dataCache.delete(CacheKeys.SETTINGS(user.id));
      console.log('🗑️ Cache do useAccessControl limpo - forçando busca no banco');
    } else {
      console.log('🔄 Revalidação manual dos dados...');
    }
    
    await Promise.all([
      loadPermissions(),
      loadRequests(),
      loadPatientSettings()
    ]);
  };

  // Função para invalidar completamente o cache após refresh
  const invalidateAllCache = () => {
    if (!user) return;
    
    console.log('🧹 [useAccessControl] Invalidando todo o cache após refresh...');
    
    // Invalidar cache específico do controle de acesso
    dataCache.delete(CacheKeys.PERMISSIONS(user.id));
    dataCache.delete(CacheKeys.REQUESTS(user.id));
    dataCache.delete(CacheKeys.SETTINGS(user.id));
    
    // Invalidar padrões relacionados ao usuário
    dataCache.invalidatePattern(`.*${user.id}.*`);
    
    console.log('✅ [useAccessControl] Cache invalidado - próximas consultas irão ao banco');
  };

  // Registrar funções de revalidação com o sistema de sincronização
  useEffect(() => {
    if (!user) return;

    const unregister = registerRevalidation(async () => {
      console.log('🔄 Revalidação automática do useAccessControl...');
      await Promise.all([
        loadPermissions(),
        loadRequests(),
        loadPatientSettings()
      ]);
    });

    return unregister;
  }, [user, registerRevalidation]);

  return {
    loading,
    permissions,
    requests,
    settings,
    hasPermission,
    requestAccess,
    reviewAccessRequest,
    updatePermissionDuration,
    updatePermissionDetails,
    revokePermission,
    updatePatientSettings,
    loadPermissions,
    loadRequests,
    loadPatientSettings,
    revalidateData,
    invalidateAllCache
  };
};

export default useAccessControl;